import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SyncIcon from "@mui/icons-material/Sync";
import { Navigate, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BusyButton } from "./shared";
import {
  getPlatformFinanceSyncRun,
  postPlatformFinanceBankAccountsSync,
  postPlatformFinanceLedgerSync,
  postPlatformFinanceSync,
} from "../../../api/client";
import {
  FINANCE_GROUP_DEFAULT_VIEW,
  FINANCE_GROUP_SLUGS,
  FINANCE_GROUP_VIEWS,
  financePath,
  PLATFORM_CONSOLE_PATH,
  resolveFinanceLegacyTab,
} from "./platformConsoleRoutes";
import {
  currentYearMonthLocal,
  FINANCE_COPY,
  FinanceCompetenciaPicker,
  FinanceViewSwitch,
  lastDayOfYearMonth,
  parseCompetencia,
} from "./FinanceUi";
import { FINANCE_PERIOD_HINT } from "./FinanceCopy";
import { FinanceMetricAiProvider } from "./FinanceMetricAi";

const SYNC_POLL_MS = 2500;
const SYNC_MAX_WAIT_MS = 15 * 60 * 1000;
const SYNC_MAX_WAIT_YEAR_MS = 90 * 60 * 1000;

/** Meses YYYY-MM do ano civil até o mês corrente (sem futuros). */
function monthsInLedgerYear(year) {
  const y = Number(year);
  if (!Number.isFinite(y) || y < 2000 || y > 2100) return [];
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  const maxMonth = y < cy ? 12 : y === cy ? cm : 0;
  const out = [];
  for (let m = 1; m <= maxMonth; m += 1) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
  }
  return out;
}

function yearSyncProgressLabel(phase, t, counts) {
  const done = Array.isArray(counts?.months_done) ? counts.months_done.length : 0;
  const total = Number(counts?.months_total) > 0 ? Number(counts.months_total) : 12;
  const year = counts?.year != null ? String(counts.year) : null;
  const fromPhase =
    phase && /^(sync_|queued_)/.test(String(phase))
      ? String(phase).replace(/^(sync_|queued_)/, "")
      : null;
  const month =
    (counts?.current_month && /^\d{4}-\d{2}$/.test(String(counts.current_month))
      ? String(counts.current_month)
      : null) ||
    (fromPhase && /^\d{4}-\d{2}$/.test(fromPhase) ? fromPhase : null);

  if (month) {
    return t("platformPage.financeSyncPhaseYearMonth", {
      defaultValue: "Sincronizando {{month}} · {{done}}/{{total}} meses…",
      month,
      done,
      total,
      year: year || month.slice(0, 4),
    });
  }
  return t("platformPage.financeSyncPhaseYear", {
    defaultValue: "Sincronizando ano {{year}} · {{done}}/{{total}} meses…",
    year: year || "—",
    done,
    total,
  });
}

function phaseLabel(phase, t, counts) {
  if (counts?.scope === "year") {
    return yearSyncProgressLabel(phase, t, counts);
  }
  const map = {
    queued: t("platformPage.financeSyncPhaseQueued", { defaultValue: "Na fila…" }),
    fetch_superlogica: t("platformPage.financeSyncPhaseFetch", {
      defaultValue: "Baixando clientes e assinaturas…",
    }),
    fetch_movements: t("platformPage.financeSyncPhaseMovements", {
      defaultValue: "Baixando cobranças e despesas do mês…",
    }),
    persist: t("platformPage.financeSyncPhasePersist", { defaultValue: "Montando DRE e KPIs…" }),
    interactions: t("platformPage.financeSyncPhaseIx", { defaultValue: "Agregando interações…" }),
    save_clients: t("platformPage.financeSyncPhaseClients", { defaultValue: "Gravando margem por cliente…" }),
    done: t("platformPage.financeSyncPhaseDone", { defaultValue: "Concluído" }),
  };
  if (phase && String(phase).startsWith("sync_")) {
    return t("platformPage.financeSyncPhaseMonthOnly", {
      defaultValue: "Sincronizando {{month}}…",
      month: String(phase).replace(/^sync_/, ""),
    });
  }
  return map[phase] || t("platformPage.financeSyncing", { defaultValue: "Sincronizando…" });
}

/** Run em andamento é do mesmo período que o usuário pediu? */
function syncRunMatchesRequest(res, { yearScope, year, month }) {
  if (!res) return false;
  const runScope = res.scope || res?.counts?.scope || null;
  const runYear = res.year != null ? Number(res.year) : Number(res?.counts?.year);
  const runMonth = res.year_month || res.period_ym || null;
  if (yearScope) {
    if (runScope === "year" && Number.isFinite(runYear)) return runYear === Number(year);
    // Run mensal antigo: só aceita se o mês for do ano pedido.
    return Boolean(runMonth && String(runMonth).startsWith(`${year}-`));
  }
  if (runScope === "year") return false;
  return Boolean(month && runMonth && String(runMonth) === String(month));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function useConsoleOutlet() {
  const ctx = useOutletContext() || {};
  return {
    registerRefresh: typeof ctx.registerRefresh === "function" ? ctx.registerRefresh : () => {},
    stopRefreshing: typeof ctx.stopRefreshing === "function" ? ctx.stopRefreshing : () => {},
    setGlobalError: typeof ctx.setGlobalError === "function" ? ctx.setGlobalError : () => {},
  };
}

function TabPanelSpinner() {
  return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }} role="status">
      <CircularProgress size={32} />
    </Box>
  );
}

const FinanceDrePanel = lazy(() => import("./FinanceDrePanel"));
const FinanceIndicatorsPanel = lazy(() => import("./FinanceIndicatorsPanel"));
const FinanceClientMarginsPanel = lazy(() => import("./FinanceClientMarginsPanel"));
const FinanceGoalsPanel = lazy(() => import("./FinanceGoalsPanel"));
const FinanceAnnualEvolutionPanel = lazy(() => import("./FinanceAnnualEvolutionPanel"));
const FinanceValuationPanel = lazy(() => import("./FinanceValuationPanel"));
const FinanceAccountMapPanel = lazy(() => import("./FinanceAccountMapPanel"));
const FinanceCashFlowPanel = lazy(() => import("./FinanceCashFlowPanel"));
const FinanceBalancePanel = lazy(() => import("./FinanceBalancePanel"));
const FinanceLedgerPanel = lazy(() => import("./FinanceLedgerPanel"));
const FinanceIncentivesPanel = lazy(() => import("./FinanceIncentivesPanel"));
const FinanceCommissionsPanel = lazy(() => import("./FinanceCommissionsPanel"));

const FINANCE_GROUPS = [
  {
    slug: "statements",
    labelKey: "platformPage.financeGroupStatements",
    defaultLabel: "Demonstrações",
    views: [
      { key: "dre", labelKey: "platformPage.financeViewDre", defaultLabel: "Resultado" },
      { key: "cash-flow", labelKey: "platformPage.financeViewCashFlow", defaultLabel: "Fluxo de caixa" },
      { key: "balance", labelKey: "platformPage.financeViewBalance", defaultLabel: "Balanço" },
      { key: "evolution", labelKey: "platformPage.financeViewEvolution", defaultLabel: "Evolução" },
    ],
  },
  {
    slug: "ledger",
    labelKey: "platformPage.financeGroupLedger",
    defaultLabel: "Movimentações",
    views: [
      { key: "entries", labelKey: "platformPage.financeViewLedgerEntries", defaultLabel: "Lançamentos" },
      { key: "reconcile", labelKey: "platformPage.financeViewReconcile", defaultLabel: "Conciliação" },
      { key: "accounts", labelKey: "platformPage.financeViewBankAccounts", defaultLabel: "Contas" },
    ],
  },
  {
    slug: "indicators",
    labelKey: "platformPage.financeGroupIndicators",
    defaultLabel: "Indicadores",
    views: [
      { key: "saas", labelKey: "platformPage.financeViewSaas", defaultLabel: "SaaS" },
      { key: "clients", labelKey: "platformPage.financeViewClients", defaultLabel: "Por cliente" },
    ],
  },
  {
    slug: "planning",
    labelKey: "platformPage.financeGroupPlanning",
    defaultLabel: "Planejamento",
    views: [
      { key: "goals", labelKey: "platformPage.financeViewGoals", defaultLabel: "Metas comerciais" },
      { key: "valuation", labelKey: "platformPage.financeViewValuation", defaultLabel: "Valor da empresa" },
    ],
  },
  {
    slug: "commissions",
    labelKey: "platformPage.financeGroupCommissions",
    defaultLabel: "Comissões",
    views: [
      { key: "sales", labelKey: "platformPage.financeViewCommSales", defaultLabel: "Vendas" },
      { key: "team-rules", labelKey: "platformPage.financeViewCommTeamRules", defaultLabel: "Réguas do time" },
      { key: "portfolio", labelKey: "platformPage.financeViewCommPortfolio", defaultLabel: "Carteira" },
      { key: "channel-rules", labelKey: "platformPage.financeViewCommChannelRules", defaultLabel: "Réguas do canal" },
      { key: "month-report", labelKey: "platformPage.financeViewCommMonthReport", defaultLabel: "Relatório do mês" },
    ],
  },
  {
    slug: "incentives",
    labelKey: "platformPage.financeGroupIncentives",
    defaultLabel: "Incentivos",
    views: [
      { key: "corporate", labelKey: "platformPage.financeViewCorporate", defaultLabel: "Corporativo" },
      { key: "people", labelKey: "platformPage.financeViewPeople", defaultLabel: "Pessoas" },
      { key: "cards", labelKey: "platformPage.financeViewCards", defaultLabel: "Cartões" },
      { key: "bonus", labelKey: "platformPage.financeViewBonus", defaultLabel: "Bônus" },
    ],
  },
  {
    slug: "setup",
    labelKey: "platformPage.financeGroupSetup",
    defaultLabel: "Configuração",
    views: [
      { key: "account-map", labelKey: "platformPage.financeViewAccountMap", defaultLabel: "Mapa de contas" },
    ],
  },
];

function panelFor(group, view) {
  if (group === "statements") {
    if (view === "cash-flow") return FinanceCashFlowPanel;
    if (view === "balance") return FinanceBalancePanel;
    if (view === "evolution") return FinanceAnnualEvolutionPanel;
    return FinanceDrePanel;
  }
  if (group === "ledger") {
    return FinanceLedgerPanel;
  }
  if (group === "indicators") {
    return view === "clients" ? FinanceClientMarginsPanel : FinanceIndicatorsPanel;
  }
  if (group === "planning") {
    return view === "valuation" ? FinanceValuationPanel : FinanceGoalsPanel;
  }
  if (group === "commissions") {
    return FinanceCommissionsPanel;
  }
  if (group === "setup") {
    return FinanceAccountMapPanel;
  }
  return FinanceIncentivesPanel;
}

function chromeFor(group, view) {
  if (group === "statements") {
    if (view === "cash-flow") return { basis: "chip-cash", periodHint: null };
    if (view === "balance") return { basis: "chip-balance", periodHint: null };
    return { basis: "toggle", periodHint: null };
  }
  if (group === "ledger") {
    if (view === "accounts") return { basis: "hidden", periodHint: null };
    return { basis: "chip-cash", periodHint: null };
  }
  if (group === "indicators") return { basis: "toggle", periodHint: null };
  if (group === "planning") {
    if (view === "goals") return { basis: "hidden", periodHint: "year" };
    return { basis: "toggle", periodHint: null };
  }
  if (group === "commissions") {
    return { basis: "hidden", periodHint: "anchorMonth" };
  }
  if (group === "incentives") {
    return { basis: "hidden", periodHint: "year" };
  }
  return { basis: "hidden", periodHint: "reference" };
}

/** Mês âncora para abas que ainda são mensais quando a competência é anual. */
function anchorMonthFromCompetencia(competencia) {
  const p = parseCompetencia(competencia);
  if (p.scope === "month") return p.value;
  const current = currentYearMonthLocal();
  const end = `${p.year}-12`;
  return end > current ? current : end;
}

export default function FinanceSectionPage() {
  const { t } = useTranslation("app");
  const { tab } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { registerRefresh, stopRefreshing, setGlobalError } = useConsoleOutlet();
  const [competencia, setCompetencia] = useState(currentYearMonthLocal);
  const [basis, setBasis] = useState("competencia");
  const [panelKey, setPanelKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [syncSeverity, setSyncSeverity] = useState("success");

  const parsed = useMemo(() => parseCompetencia(competencia), [competencia]);
  const yearScope = parsed.scope === "year";
  const month = yearScope ? null : parsed.value;
  const year = yearScope ? parsed.year : null;
  const anchorMonth = useMemo(() => anchorMonthFromCompetencia(competencia), [competencia]);

  const tabSlug = String(tab || "statements").toLowerCase();
  const legacy = resolveFinanceLegacyTab(tabSlug);
  const groupSlug = FINANCE_GROUP_SLUGS.includes(tabSlug) ? tabSlug : null;
  const group = FINANCE_GROUPS.find((g) => g.slug === groupSlug) || FINANCE_GROUPS[0];
  const allowedViews = FINANCE_GROUP_VIEWS[group.slug] || [];
  const defaultView = FINANCE_GROUP_DEFAULT_VIEW[group.slug];
  const requestedViewRaw = params.get("view");
  const COMMISSION_VIEW_ALIASES = { attributions: "sales", rules: "channel-rules" };
  const requestedView =
    group.slug === "commissions" && COMMISSION_VIEW_ALIASES[requestedViewRaw]
      ? COMMISSION_VIEW_ALIASES[requestedViewRaw]
      : requestedViewRaw;
  const innerView = allowedViews.includes(requestedView) ? requestedView : defaultView;
  const tabIndex = FINANCE_GROUPS.findIndex((g) => g.slug === group.slug);
  const chrome = chromeFor(group.slug, innerView);
  const Panel = useMemo(() => panelFor(group.slug, innerView), [group.slug, innerView]);

  const setInnerView = (key) => {
    const next = new URLSearchParams(params);
    next.set("view", key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    registerRefresh(() => setPanelKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  if (legacy) {
    if (legacy.group === "setup" && legacy.view === "prices") {
      return <Navigate to={`${PLATFORM_CONSOLE_PATH.catalog}?area=composer`} replace />;
    }
    return <Navigate to={financePath(legacy.group, legacy.view)} replace />;
  }
  if (groupSlug === "incentives" && requestedViewRaw === "channel") {
    return <Navigate to={financePath("commissions", "sales")} replace />;
  }
  if (
    groupSlug === "commissions" &&
    (requestedViewRaw === "attributions" || requestedViewRaw === "rules")
  ) {
    return <Navigate to={financePath("commissions", requestedView)} replace />;
  }
  if (groupSlug === "commissions" && requestedViewRaw === "my-results") {
    return <Navigate to={PLATFORM_CONSOLE_PATH.myIncentives} replace />;
  }
  if (!groupSlug) {
    return <Navigate to={PLATFORM_CONSOLE_PATH.financeStatements} replace />;
  }
  // URLs antigas do simulador no Financeiro → Catálogo e operações.
  if (groupSlug === "setup" && requestedView === "prices") {
    return <Navigate to={`${PLATFORM_CONSOLE_PATH.catalog}?area=composer`} replace />;
  }

  const syncTooltip =
    group.slug === "ledger"
      ? innerView === "accounts"
        ? FINANCE_COPY.syncTooltipAccounts
        : FINANCE_COPY.syncTooltipLedger
      : FINANCE_COPY.syncTooltipStatements;

  /** Movimentações: sincroniza livro-caixa / contas (não o snapshot DRE). */
  const handleLedgerSync = async () => {
    if (innerView === "accounts") {
      setSyncSeverity("info");
      setSyncMsg(
        t("platformPage.financeLedgerAccountsSyncing", {
          defaultValue: "Importando contas do ERP…",
        })
      );
      const res = await postPlatformFinanceBankAccountsSync({});
      setSyncSeverity("success");
      setSyncMsg(
        t("platformPage.financeLedgerAccountsSyncOk", {
          defaultValue:
            "Contas do ERP: {{created}} novas, {{updated}} atualizadas ({{fetched}} no ERP).",
          created: res?.counts?.created ?? 0,
          updated: res?.counts?.updated ?? 0,
          fetched: res?.counts?.fetched ?? 0,
        })
      );
      setPanelKey((k) => k + 1);
      return;
    }

    const monthsToSync = yearScope ? monthsInLedgerYear(Number(year)) : month ? [month] : [];
    if (!monthsToSync.length) {
      throw new Error(
        t("platformPage.financeLedgerSyncInvalidPeriod", {
          defaultValue: "Selecione um período válido para atualizar o ERP.",
        })
      );
    }

    // Ano: importa contas 1× e nos meses seguintes pula o re-sync (orçamento ~29s da API).
    const multiMonth = monthsToSync.length > 1;
    if (multiMonth) {
      setSyncSeverity("info");
      setSyncMsg(
        t("platformPage.financeLedgerAccountsSyncing", {
          defaultValue: "Importando contas do ERP…",
        })
      );
      try {
        await postPlatformFinanceBankAccountsSync({});
      } catch (err) {
        // Best-effort: meses ainda rodam; contas já cadastradas bastam.
        console.warn("[finance] pré-sync contas:", err?.response?.data?.message || err?.message || err);
      }
    }

    let created = 0;
    let updated = 0;
    let skipped = 0;
    const failedMonths = [];
    for (let i = 0; i < monthsToSync.length; i += 1) {
      const ym = monthsToSync[i];
      setSyncSeverity("info");
      setSyncMsg(
        multiMonth
          ? t("platformPage.financeLedgerSyncProgress", {
              defaultValue: "Atualizando ERP {{month}} · {{done}}/{{total}} meses…",
              month: ym,
              done: i + 1,
              total: monthsToSync.length,
            })
          : t("platformPage.financeLedgerSyncProgressMonth", {
              defaultValue: "Atualizando lançamentos do ERP ({{month}})…",
              month: ym,
            })
      );
      try {
        const res = await postPlatformFinanceLedgerSync({
          month: ym,
          ...(multiMonth ? { skip_bank_accounts: true } : {}),
        });
        created += Number(res?.counts?.created || 0);
        updated += Number(res?.counts?.updated || 0);
        skipped += Number(res?.counts?.skipped || 0);
      } catch (err) {
        const apiMsg = String(err?.response?.data?.message || err?.message || "").trim();
        // Só substitui por texto de timeout quando a API não trouxe causa útil.
        const generic =
          !apiMsg ||
          /^internal server error$/i.test(apiMsg) ||
          /endpoint request timed out|econnaborted|timeout of \d+ms exceeded/i.test(apiMsg);
        const detail = generic
          ? t("platformPage.financeLedgerSyncMonthTimeout", {
              defaultValue:
                "tempo esgotado no servidor (mês com muitos lançamentos). Tente de novo ou sincronize só este mês.",
            })
          : apiMsg;
        failedMonths.push({ month: ym, detail: String(detail).slice(0, 180) });
        if (!multiMonth) {
          throw new Error(
            t("platformPage.financeLedgerSyncMonthFailed", {
              defaultValue: "Falha em {{month}}: {{detail}}",
              month: ym,
              detail,
            })
          );
        }
      }
    }

    if (failedMonths.length && failedMonths.length === monthsToSync.length) {
      throw new Error(
        t("platformPage.financeLedgerSyncYearAllFailed", {
          defaultValue: "Nenhum mês de {{year}} pôde ser atualizado. Ex.: {{detail}}",
          year: year || monthsToSync[0]?.slice(0, 4),
          detail: failedMonths[0]?.detail || "erro no servidor",
        })
      );
    }

    if (failedMonths.length) {
      setSyncSeverity("warning");
      setSyncMsg(
        t("platformPage.financeLedgerSyncYearPartial", {
          defaultValue:
            "ERP parcial ({{ok}}/{{total}} meses): {{created}} novos, {{updated}} atualizados. Falharam: {{failed}}.",
          ok: monthsToSync.length - failedMonths.length,
          total: monthsToSync.length,
          created,
          updated,
          failed: failedMonths.map((f) => f.month).join(", "),
        })
      );
      setPanelKey((k) => k + 1);
      return;
    }

    setSyncSeverity("success");
    setSyncMsg(
      multiMonth
        ? t("platformPage.financeLedgerSyncYearOk", {
            defaultValue:
              "ERP ({{months}} meses): {{created}} novos, {{updated}} atualizados, {{skipped}} ignorados.",
            months: monthsToSync.length,
            created,
            updated,
            skipped,
          })
        : t("platformPage.financeLedgerSyncOk", {
            defaultValue: "ERP: {{created}} novos, {{updated}} atualizados, {{skipped}} ignorados.",
            created,
            updated,
            skipped,
          })
    );
    setPanelKey((k) => k + 1);
  };

  /** Demonstrações / indicadores / planejamento: snapshot gerencial assíncrono. */
  const handleStatementsSync = async () => {
    const payload = yearScope ? { year } : { month };
    const res = await postPlatformFinanceSync(payload);
    const runId = res?.run_id;
    const scopeYear = res?.scope === "year" || yearScope;
    const display = scopeYear ? String(res?.year || year || "") : res?.year_month || month;

    if (res?.conflict || (res?.already_running && !syncRunMatchesRequest(res, { yearScope, year, month }))) {
      throw new Error(
        res?.message ||
          t("platformPage.financeSyncConflict", {
            defaultValue:
              "Já existe uma atualização do ERP em andamento para outro período. Aguarde a conclusão e tente de novo.",
          })
      );
    }

    if (res?.ok && !res?.accepted && res?.synced_at) {
      setSyncSeverity("success");
      setSyncMsg(
        scopeYear
          ? t("platformPage.financeSyncOkYear", {
              defaultValue: "Dados de {{year}} atualizados com o ERP.",
              year: display,
            })
          : t("platformPage.financeSyncOk", {
              defaultValue: "Dados de {{month}} atualizados com o ERP.",
              month: display,
            })
      );
      setPanelKey((k) => k + 1);
      return;
    }

    if (!runId) {
      throw new Error(
        res?.message ||
          t("platformPage.financeSyncError", {
            defaultValue: "Não foi possível iniciar a atualização do ERP.",
          })
      );
    }

    setSyncSeverity("info");
    setSyncMsg(
      scopeYear
        ? t("platformPage.financeSyncQueuedYear", {
            defaultValue: "Atualizando ERP do ano {{year}} (pode levar vários minutos)…",
            year: display,
          })
        : t("platformPage.financeSyncQueued", {
            defaultValue: "Atualizando ERP de {{month}} (pode levar alguns minutos)…",
            month: display,
          })
    );

    const started = Date.now();
    const maxWait = scopeYear ? SYNC_MAX_WAIT_YEAR_MS : SYNC_MAX_WAIT_MS;
    let final = null;
    while (Date.now() - started < maxWait) {
      await sleep(SYNC_POLL_MS);
      const polled = await getPlatformFinanceSyncRun(runId);
      const item = polled?.item || polled;
      if (!item) continue;
      const counts = item?.counts || {};
      const phase = counts?.phase;
      const clients = counts?.clients;
      const invoices = counts?.invoices;
      let detail = "";
      if (counts?.scope !== "year" && (clients != null || invoices != null)) {
        detail = ` · ${clients ?? "—"} clientes · ${invoices ?? "—"} cobranças`;
      }
      if (
        scopeYear &&
        counts?.scope === "year" &&
        counts?.year != null &&
        Number(counts.year) !== Number(year)
      ) {
        setSyncMsg(
          t("platformPage.financeSyncWrongYear", {
            defaultValue:
              "Acompanhando atualização do ano {{runYear}} (você pediu {{year}}). Aguarde ou tente de novo depois.",
            runYear: counts.year,
            year,
          })
        );
      } else {
        setSyncMsg(`${phaseLabel(phase, t, counts)}${detail}`);
      }

      if (item.status === "success") {
        final = item;
        break;
      }
      if (item.status === "error") {
        throw new Error(item.error_message || "Falha na atualização do ERP.");
      }
    }

    if (!final) {
      setSyncSeverity("info");
      setSyncMsg(
        t("platformPage.financeSyncTimeout", {
          defaultValue:
            "A atualização ainda está rodando no servidor. Recarregue a página em alguns minutos para ver o resultado.",
        })
      );
      setPanelKey((k) => k + 1);
      return;
    }

    setSyncSeverity("success");
    const finalCounts = final?.counts || {};
    setSyncMsg(
      scopeYear || finalCounts.scope === "year"
        ? t("platformPage.financeSyncOkYear", {
            defaultValue: "Dados de {{year}} atualizados com o ERP.",
            year: finalCounts.year || display,
          })
        : t("platformPage.financeSyncOk", {
            defaultValue: "Dados de {{month}} atualizados com o ERP.",
            month: final.year_month || display,
          })
    );
    setPanelKey((k) => k + 1);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    setSyncSeverity("info");
    setGlobalError(null);
    try {
      if (group.slug === "ledger") {
        await handleLedgerSync();
      } else {
        await handleStatementsSync();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("platformPage.financeSyncError", {
          defaultValue: "Não foi possível sincronizar com o ERP. Verifique os tokens e tente de novo.",
        });
      setSyncSeverity("error");
      // Um único alerta na área do Financeiro (evita duplicar com o banner global).
      setSyncMsg(msg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        borderColor: "divider",
        overflow: "hidden",
        p: { xs: 2, md: 2.5 },
      })}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0, maxWidth: 560 }}>
          <Typography variant="h6" sx={{ mb: 0.25, fontWeight: 700, letterSpacing: -0.02 }}>
            {t("platformPage.navFinance", { defaultValue: "Financeiro" })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {group.slug === "ledger"
              ? t("platformPage.financeIntroLedger", {
                  defaultValue: "Caixa e conciliação a partir do ERP.",
                })
              : t("platformPage.financeIntro", {
                  defaultValue: "Leitura gerencial a partir do ERP.",
                })}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "flex-end" }}
          flexShrink={0}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <FinanceCompetenciaPicker
            value={competencia}
            onChange={setCompetencia}
            disabled={syncing}
            label={t("platformPage.financePeriod", { defaultValue: "Período" })}
          />
          {chrome.basis === "chip-cash" || chrome.basis === "chip-balance" ? (
            <Tooltip
              title={
                chrome.basis === "chip-cash"
                  ? FINANCE_PERIOD_HINT.cashAlways
                  : FINANCE_PERIOD_HINT.balancePosition
              }
            >
              <Chip
                size="small"
                variant="outlined"
                label={chrome.basis === "chip-cash" ? "Regime: caixa" : "Posição patrimonial"}
                sx={{ height: 42, borderRadius: 2, px: 0.5, fontWeight: 600 }}
              />
            </Tooltip>
          ) : chrome.basis === "toggle" ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={basis}
              disabled={syncing}
              onChange={(_, v) => {
                if (v) setBasis(v);
              }}
              aria-label={t("platformPage.financeDreBasis", { defaultValue: "Regime contábil" })}
              sx={{ height: 42 }}
            >
              <ToggleButton
                value="competencia"
                sx={{ px: 1.5, textTransform: "none", fontWeight: 600 }}
                title={FINANCE_COPY.basisCompetenceHint}
              >
                {t("platformPage.financeDreBasisCompetence", { defaultValue: "Competência" })}
              </ToggleButton>
              <ToggleButton
                value="caixa"
                sx={{ px: 1.5, textTransform: "none", fontWeight: 600 }}
                title={FINANCE_COPY.basisCashHint}
              >
                {t("platformPage.financeDreBasisCash", { defaultValue: "Caixa" })}
              </ToggleButton>
            </ToggleButtonGroup>
          ) : chrome.periodHint ? (
            <Tooltip title={FINANCE_PERIOD_HINT[chrome.periodHint] || ""}>
              <Chip
                size="small"
                variant="outlined"
                label={
                  chrome.periodHint === "year"
                    ? "Vista anual"
                    : chrome.periodHint === "anchorMonth"
                      ? "Mês âncora"
                      : "Período de referência"
                }
                sx={{ height: 42, borderRadius: 2, px: 0.5, fontWeight: 600 }}
              />
            </Tooltip>
          ) : null}
          <Tooltip title={syncTooltip}>
            <span>
              <BusyButton
                variant="contained"
                loading={syncing}
                onClick={handleSync}
                startIcon={<SyncIcon />}
                loadingText={t("platformPage.financeSyncing", { defaultValue: "Atualizando…" })}
                sx={{ whiteSpace: "nowrap", height: 42, px: 2, textTransform: "none", fontWeight: 600 }}
              >
                {t("platformPage.financeSync", { defaultValue: "Atualizar ERP" })}
              </BusyButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {syncMsg ? (
        <Alert severity={syncSeverity} sx={{ mb: 2 }} onClose={() => setSyncMsg(null)}>
          {syncMsg}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "flex-end" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tabs
          value={tabIndex < 0 ? 0 : tabIndex}
          onChange={(_, i) => navigate(financePath(FINANCE_GROUPS[i].slug))}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            minWidth: 0,
            flex: 1,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
          }}
        >
          {FINANCE_GROUPS.map((tabItem) => (
            <Tab key={tabItem.slug} label={t(tabItem.labelKey, { defaultValue: tabItem.defaultLabel })} />
          ))}
        </Tabs>
        {group.views?.length > 1 ? (
          <FinanceViewSwitch
            value={innerView}
            onChange={setInnerView}
            ariaLabel={t("platformPage.financeViewSwitch", { defaultValue: "Vista" })}
            options={group.views.map((v) => ({
              key: v.key,
              label: t(v.labelKey, { defaultValue: v.defaultLabel }),
            }))}
          />
        ) : null}
      </Stack>

      <Suspense fallback={<TabPanelSpinner />}>
        <FinanceMetricAiProvider
          period={
            yearScope
              ? { start: `${year}-01-01`, end: `${year}-12-31` }
              : {
                  start: `${month || anchorMonth}-01`,
                  end: lastDayOfYearMonth(month || anchorMonth) || `${month || anchorMonth}-28`,
                }
          }
          summaryContext={{
            tab: group.slug,
            view: innerView,
            competencia,
            basis,
            scope: yearScope ? "year" : "month",
            year: year || null,
            month: month || anchorMonth,
          }}
        >
          <Panel
            key={`${panelKey}-${competencia}-${group.slug}-${innerView}-${basis}`}
            month={month || anchorMonth}
            year={year}
            competencia={competencia}
            anchorMonth={anchorMonth}
            basis={basis}
            view={innerView}
          />
        </FinanceMetricAiProvider>
      </Suspense>
    </Paper>
  );
}
